// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

type Props = PageProps & HasShellContext & ThemeProps<BaseTheme>;

export interface StorePickupOverlayStyles {
    page?: MobiusPageProps;
    drawer?: DrawerPresentationProps;
    innerWrapper?: InjectableCss;
}

export const storePickupOverlayStyles: StorePickupOverlayStyles = {
    page: {
        padding: 0,
    },
    drawer: {
        size: 400,
        closeButtonProps: {
            shape: "pill",
            buttonType: "solid",
            color: "common.background",
            size: 36,
        },
        cssOverrides: {
            drawerTitle: css`
                background: ${getColor("common.background")};
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerContent: css`
                overflow-y: initial;
            `,
        },
    },
};

const InnerWrapper = styled.div<{ isEdit?: boolean } & InjectableCss>`
    padding: ${props => (props.isEdit ? "0" : "0 30px")};
    overflow: ${props => (props.isEdit ? "auto" : "hidden")};
    width: 100%;
    margin: 0 auto;
    ${injectCss}
`;

const styles = storePickupOverlayStyles;

const StorePickupOverlay = ({ id, shellContext: { isCurrentPage, isEditing, isInShell } }: Props) => {
    const isShell = isCurrentPage && isInShell;
    const isEdit = isShell && isEditing;
    const isOpen = useAppSelector(state => state.components.storePickupOverlay.isOpen);
    const dispatch = useAppDispatch();
    const closeDrawer = () => {
        // XNG-Changes: passed an extra parameter
        dispatch(setStorePickupOverlayIsOpen({ isOpen: false, showBranchesOnly: false } as any));
    };

    const location = useAppSelector(o => getLocation(o));
    useEffect(() => {
        dispatch(setStorePickupOverlayIsOpen({ isOpen: false, showBranchesOnly: false } as any));
    }, [location.pathname]);

    return (
        <Page {...styles.page}>
            <Drawer
                {...styles.drawer}
                position="right"
                isOpen={isOpen || isShell}
                handleClose={closeDrawer}
                contentLabel="store pickup drawer"
                data-test-selector="storePickupDrawer"
            >
                <InnerWrapper isEdit={isEdit} {...styles.innerWrapper}>
                    <Zone contentId={id} zoneName="Content" />
                </InnerWrapper>
            </Drawer>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withTheme(withIsInShell(StorePickupOverlay)),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
